import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../urls';
import DisputesModal from './disputes-modal';
import '@/theme/App.css';
import { AppDataGrid } from '../ui';
import { formatDate } from '@/lib/formatters';
import { getUser } from '@/lib/common';
import { Box, styled, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Pill } from '@/components/ds';
import { statusTone, T } from '@/theme/tokens';

type StatusFilter = 'Draft' | 'Open' | 'Closed';

const UnreadDot = styled('span')({
  width: 7,
  height: 7,
  borderRadius: '50%',
  backgroundColor: T.danger,
  display: 'inline-block',
  marginLeft: 4,
  verticalAlign: 'middle',
  flexShrink: 0,
});

const SubjectCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

const columns = [
  {
    field: 'id',
    headerName: 'Dispute ID',
    width: 100,
  },
  {
    field: 'subject',
    headerName: 'Dispute Subject',
    width: 350,
    renderCell: (params: any) => (
      <SubjectCell>
        <span>{params.value}</span>
        {params.row?.has_unread && <UnreadDot />}
      </SubjectCell>
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.5,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.25,
    renderCell: (params: any) =>
      params.value ? (
        <Pill tone={statusTone(params.value)}>{params.value}</Pill>
      ) : null,
  },
  {
    field: 'date_created_txt',
    headerName: 'Created At',
    flex: 0.6,
    valueGetter: (params: any) => {
      return params.value ? formatDate(new Date(params.value)) : null;
    },
  },
  {
    field: 'sender',
    headerName: 'Dispute Sender',
    flex: 0.5,
  },

  {
    field: 'receiver',
    headerName: 'Dispute Receiver',
    flex: 0.5,
  },
];

/** Loads a single dispute with context from the API. Returns { dispute, context } or null. */
async function loadDispute(disputeId: string | number): Promise<{
  dispute: any;
  context?: { initial: any; current: any; snapshots?: any[] };
} | null> {
  const response: any = await fetchWrapper.get(
    urls.DevOps.getDispute.replace(':id', String(disputeId)),
  );
  const dispute = response?.dispute ?? null;
  if (!dispute) return null;
  return {
    dispute,
    context: response?.context,
  };
}

export default function DisputesTable(props: any) {
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [dispute, setDispute] = useState<any>(null);
  const [disputeContext, setDisputeContext] = useState<{
    initial: any;
    current: any;
    snapshots?: any[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  function handleCloseModal(_event: any, _data: any) {
    setModalIsOpen(false);
    setDispute(null);
    setDisputeContext(null);
  }

  // table functions
  const [items, setItems] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('Open');
  const user = getUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilterCounts = useMemo(() => {
    if (!Array.isArray(items)) return { open: 0, draft: 0, closed: 0 };
    const typeFiltered = items.filter((row: any) =>
      (props.Filter ?? '') === '' ? true : row.type?.includes(props.Filter),
    );
    return {
      open: typeFiltered.filter((row: any) => row.status === 'Open').length,
      draft: typeFiltered.filter((row: any) => row.status === 'Draft').length,
      closed: typeFiltered.filter((row: any) => row.status === 'Closed').length,
    };
  }, [items, props.Filter]);

  const itemsFiltered = useMemo(() => {
    const byType = items.filter((row: any) =>
      (props.Filter ?? '') === '' ? true : row.type?.includes(props.Filter),
    );
    return byType.filter((row: any) => row.status === statusFilter);
  }, [items, props.Filter, statusFilter]);

  const handleStatusFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: StatusFilter | null,
  ) => {
    if (newFilter !== null) setStatusFilter(newFilter);
  };

  useEffect(() => {
    setLoading(true);
    fetchWrapper
      .post(urls.DevOps.getMyDisputeList, { id: user?.id })
      .then((data) => {
        setItems(data);
        // Check for dispute URL parameter and open modal if found
        const disputeIdParam = searchParams.get('dispute');
        if (disputeIdParam) {
          loadDispute(disputeIdParam)
            .then((result) => {
              if (result) {
                setDispute(result.dispute);
                setDisputeContext(result.context ?? null);
                setModalIsOpen(true);
              }
            })
            .catch((error) => console.error('There was an error!', error));
          searchParams.delete('dispute');
          setSearchParams(searchParams, { replace: true });
        }
      })
      .catch((error) => console.error('There was an error!', error))
      .finally(() => setLoading(false));
  }, []);

  // Add effect to refresh data when modal closes
  useEffect(() => {
    if (!modalIsOpen && !loading) {
      fetchWrapper
        .post(urls.DevOps.getMyDisputeList, { id: user?.id })
        .then((data) => setItems(data))
        .catch((error) => console.error('There was an error!', error));
    }
  }, [modalIsOpen]);

  return (
    <div style={{ height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 1,
        }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={handleStatusFilterChange}
          size="small"
          color="primary">
          <ToggleButton value="Open" disabled={statusFilterCounts.open === 0}>
            Open ({statusFilterCounts.open})
          </ToggleButton>
          <ToggleButton value="Draft" disabled={statusFilterCounts.draft === 0}>
            Draft ({statusFilterCounts.draft})
          </ToggleButton>
          <ToggleButton value="Closed" disabled={statusFilterCounts.closed === 0}>
            Closed ({statusFilterCounts.closed})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <AppDataGrid
        style={{ width: '100%', height: '388px', margin: '0px', border: 0 }}
        rows={itemsFiltered}
        columns={columns}
        loading={loading}
        getRowClassName={(params) =>
          params.row?.has_unread ? 'dispute-row-unread' : ''
        }
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5, page: 0 },
          },
        }}
        onRowClick={(params) => {
          const rowId = params.row?.id;
          if (rowId) {
            loadDispute(rowId)
              .then((result) => {
                if (result) {
                  setDispute(result.dispute);
                  setDisputeContext(result.context ?? null);
                  setModalIsOpen(true);
                }
              })
              .catch((error) => console.error('There was an error!', error));
          }
        }}
        // @ts-ignore
        onSelectionModelChange={(ids: any) => {
          const selectedIDs = new Set(ids);
          const selectedRowData = itemsFiltered.filter((row: any) =>
            selectedIDs.has(row.id),
          );
          const rowId = selectedRowData[0]?.id;
          if (rowId) {
            loadDispute(rowId)
              .then((result) => {
                if (result) {
                  setDispute(result.dispute);
                  setDisputeContext(result.context ?? null);
                  setModalIsOpen(true);
                }
              })
              .catch((error) => console.error('There was an error!', error));
          }
        }}
      />

      {dispute && (
        <DisputesModal
          Dispute={dispute}
          initialContext={disputeContext}
          IsModalOpened={modalIsOpen}
          onCloseModal={handleCloseModal}
        />
      )}
    </div>
  );
}
